import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Container, Row, Col, Card, Badge, Form, Accordion, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import { getTransactions, getKpiSettings, updateKpiSettings } from '../api';
import { toast } from 'react-toastify';

const PIE_COLORS = ['#00C49F', '#FFBB28', '#FF8042']; 

const KpiUnpaidPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [globalCategoryFilter, setGlobalCategoryFilter] = useState('ALL');
  const [globalStatusFilter, setGlobalStatusFilter] = useState('ALL'); 
  const [selectedCategory, setSelectedCategory] = useState(null); 
  const [selectedAgeRange, setSelectedAgeRange] = useState(null);

  const [salesMonth1, setSalesMonth1] = useState('');
  const [salesMonth2, setSalesMonth2] = useState('');
  const [salesMonth3, setSalesMonth3] = useState('');
  const debounceTimeout = useRef(null);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const [transData, settingsData] = await Promise.all([
          getTransactions(),
          getKpiSettings()
        ]);
        setTransactions(transData);
        if (settingsData) {
          setSalesMonth1(settingsData.salesMonth1 || '');
          setSalesMonth2(settingsData.salesMonth2 || '');
          setSalesMonth3(settingsData.salesMonth3 || '');
        }
      } catch (err) {
        setError('Error al cargar los datos iniciales.');
        toast.error('Error al cargar los datos para la página de KPIs.');
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  const handleSalesChange = (setter, key, value) => {
    setter(value);
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(async () => {
      try {
        await updateKpiSettings({ [key]: value });
        toast.success(`Valor guardado.`);
      } catch (err) {}
    }, 1000);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(value);
  };

  const formatCurrencyAbbreviated = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}k`;
    return value;
  };

  const today = new Date();
  today.setHours(0,0,0,0);

  const getEffectiveDueDate = (transaction) => {
    if (!transaction.date) return new Date(); 
    const [year, month, day] = transaction.date.split('-').map(Number);
    const localDate = new Date(year, month - 1, day); 
    if (transaction.status === 'Esperando OC/HES') localDate.setDate(localDate.getDate() + 14);
    return localDate;
  };

  const getDaysOverdue = (transaction) => {
    const effectiveDate = getEffectiveDueDate(transaction);
    const diffTime = today - effectiveDate; 
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const isTransactionOverdue = (transaction) => {
      const validStatuses = ['pendiente', 'Esperando OC/HES', 'Por Procesar'];
      if (!validStatuses.includes(transaction.status)) return false;
      const effectiveDate = getEffectiveDueDate(transaction);
      return effectiveDate < today;
  };

  const allOverdueTransactions = useMemo(() => {
    return transactions.filter(t => isTransactionOverdue(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions]); 

  const availableCategories = useMemo(() => {
    const cats = new Set(allOverdueTransactions.map(t => t.category || 'Sin Categoría'));
    return Array.from(cats).sort();
  }, [allOverdueTransactions]);

  const overdueTransactions = useMemo(() => {
    return allOverdueTransactions.filter(t => {
      const matchCategory = globalCategoryFilter === 'ALL' || (t.category || 'Sin Categoría') === globalCategoryFilter;
      const matchStatus = globalStatusFilter === 'ALL' || t.status === globalStatusFilter;
      return matchCategory && matchStatus;
    });
  }, [allOverdueTransactions, globalCategoryFilter, globalStatusFilter]);

  const totalOverdueAmount = overdueTransactions.reduce((sum, t) => t.type === 'ingreso' ? sum + t.amount : sum - t.amount, 0);

  const categoryData = useMemo(() => {
    const acc = overdueTransactions.reduce((acc, t) => {
      const category = t.category || 'Sin Categoría';
      const impact = t.type === 'ingreso' ? t.amount : -t.amount;
      acc[category] = (acc[category] || 0) + impact;
      return acc;
    }, {});
    return Object.entries(acc).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [overdueTransactions]);

  const ageData = useMemo(() => {
    const counts = { '0-30 días': 0, '31-60 días': 0, '>60 días': 0 };
    overdueTransactions.forEach(t => {
      const days = getDaysOverdue(t);
      const val = t.amount;
      if (days <= 30) counts['0-30 días'] += val;
      else if (days <= 60) counts['31-60 días'] += val;
      else counts['>60 días'] += val;
    });
    return [
      { name: '0-30 días', value: counts['0-30 días'] },
      { name: '31-60 días', value: counts['31-60 días'] },
      { name: '>60 días', value: counts['>60 días'] },
    ].filter(d => d.value > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overdueTransactions]);

  const debtVsSalesPieData = useMemo(() => {
     const counts = { '0-30 días': 0, '31-60 días': 0, '>60 días': 0 };
     overdueTransactions.forEach(t => {
       if (t.type === 'ingreso') {
         const days = getDaysOverdue(t);
         if (days <= 30) counts['0-30 días'] += t.amount;
         else if (days <= 60) counts['31-60 días'] += t.amount;
         else counts['>60 días'] += t.amount;
       }
     });

     const createPieData = (deuda, ventas) => {
        if (ventas <= 0) return [{ name: 'Deuda Vencida', value: deuda }]; 
        const remainingSales = Math.max(0, ventas - deuda);
        return [
          { name: 'Deuda Vencida', value: deuda },
          { name: 'Ventas Restantes', value: remainingSales }
        ].filter(d => d.value > 0);
     };

     return [
        { period: '0-30 días', salesLabel: 'Mes Anterior', data: createPieData(counts['0-30 días'], parseFloat(salesMonth1) || 0) },
        { period: '31-60 días', salesLabel: 'Hace 2 Meses', data: createPieData(counts['31-60 días'], parseFloat(salesMonth2) || 0) },
        { period: '>60 días', salesLabel: 'Hace 3 Meses', data: createPieData(counts['>60 días'], parseFloat(salesMonth3) || 0) },
     ];
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overdueTransactions, salesMonth1, salesMonth2, salesMonth3]);

  const filteredTransactions = useMemo(() => {
    return overdueTransactions.filter(t => {
      let matchesCategory = true;
      let matchesAge = true;
      if (selectedCategory) matchesCategory = (t.category || 'Sin Categoría') === selectedCategory;
      if (selectedAgeRange) {
        const days = getDaysOverdue(t);
        if (selectedAgeRange === '0-30 días') matchesAge = days <= 30;
        else if (selectedAgeRange === '31-60 días') matchesAge = days > 30 && days <= 60;
        else if (selectedAgeRange === '>60 días') matchesAge = days > 60;
      }
      return matchesCategory && matchesAge;
    }).sort((a, b) => new Date(a.date) - new Date(b.date)); 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overdueTransactions, selectedCategory, selectedAgeRange]);


  if (loading) return <Container className="py-4 text-center"><p>Cargando Dashboard...</p></Container>;
  if (error) return <Container className="py-4 text-center text-danger"><p>{error}</p></Container>;

  return (
    <Container fluid className="py-4">
      <Row className="align-items-center mb-4">
        <Col md={5}>
          <h2 className="mb-0">Dashboard de Deuda Morosa</h2>
          <small className="text-muted">Vista Ejecutiva Semanal</small>
        </Col>
        <Col md={7} className="d-flex justify-content-end align-items-center gap-2">
            <Form.Select value={globalStatusFilter} onChange={(e) => setGlobalStatusFilter(e.target.value)} style={{ maxWidth: '200px' }} className="border-primary">
              <option value="ALL">Todos los Estados</option>
              <option value="pendiente">Solo Pendientes</option>
              <option value="Esperando OC/HES">Solo Esperando OC/HES</option>
              <option value="Por Procesar">Solo Por Procesar</option>
            </Form.Select>
            <Form.Select value={globalCategoryFilter} onChange={(e) => setGlobalCategoryFilter(e.target.value)} style={{ maxWidth: '200px' }}>
              <option value="ALL">Todas las Categorías</option>
              {availableCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </Form.Select>
            <Link to="/" className="btn btn-outline-secondary ms-2">&larr; Volver</Link>
        </Col>
      </Row>

      <Row className="mb-4 g-3">
        <Col md={4}>
          <Card className="text-center h-100 border-danger shadow-sm">
            <Card.Body>
              <Card.Title className="text-muted text-uppercase small">Total Deuda Morosa</Card.Title>
              <h2 className={`display-4 fw-bold mb-0 ${totalOverdueAmount >= 0 ? 'text-danger' : 'text-success'}`}>
                {formatCurrency(totalOverdueAmount)}
              </h2>
              <small className="text-muted">{overdueTransactions.length} transacciones</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center h-100 shadow-sm">
            <Card.Body>
              <Card.Title className="text-muted text-uppercase small">Antigüedad Crítica (&gt;60 Días)</Card.Title>
              <h3 className="display-6 text-warning fw-bold mb-0">
                {formatCurrency(ageData.find(d => d.name === '>60 días')?.value || 0)}
              </h3>
              <small className="text-muted">Requiere atención inmediata</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="h-100 shadow-sm">
             <Accordion flush>
                <Accordion.Item eventKey="0">
                  <Accordion.Header>Configuración KPIs</Accordion.Header>
                  <Accordion.Body>
                     <Form.Control size="sm" type="number" className="mb-1" value={salesMonth1} onChange={(e) => handleSalesChange(setSalesMonth1, 'salesMonth1', e.target.value)} />
                     <Form.Control size="sm" type="number" className="mb-1" value={salesMonth2} onChange={(e) => handleSalesChange(setSalesMonth2, 'salesMonth2', e.target.value)} />
                     <Form.Control size="sm" type="number" value={salesMonth3} onChange={(e) => handleSalesChange(setSalesMonth3, 'salesMonth3', e.target.value)} />
                  </Accordion.Body>
                </Accordion.Item>
             </Accordion>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4 g-3">
        <Col lg={7}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={categoryData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={100} />
                    <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="value" fill="#0088FE" onClick={(data) => setSelectedCategory(data.name === selectedCategory ? null : data.name)} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={5}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={ageData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      onClick={(data) => setSelectedAgeRange(data.name === selectedAgeRange ? null : data.name)}
                    >
                      {ageData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Legend />
                    <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4 g-3">
        <Col md={12}>
            <Card className="shadow-sm">
                <Card.Body>
                    <Card.Title className="mb-3">Impacto de Deuda sobre Ventas por Período</Card.Title>
                    <Row>
                        {debtVsSalesPieData.map((data, index) => (
                            <Col md={4} key={index}>
                                <h6 className="text-center text-muted">{data.period} (vs {data.salesLabel})</h6>
                                <div style={{ height: 250 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={data.data}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                outerRadius={80}
                                                fill="#8884d8"
                                                dataKey="value"
                                                label={({ name, value, percent }) => 
                                                  value > 0 
                                                  ? `${(percent * 100).toFixed(0)}% (${formatCurrencyAbbreviated(value)})` 
                                                  : ''
                                                }
                                            >
                                                {data.data.map((entry, idx) => (
                                                  <Cell key={`cell-${idx}`} fill={entry.name === 'Deuda Vencida' ? '#FF8042' : '#82ca9d'} />
                                                ))}
                                            </Pie>
                                            <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                                            <Legend />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Card.Body>
            </Card>
        </Col>
      </Row>

      <Card className="shadow-sm">
        <Card.Body className="p-0">
          <Table hover striped responsive className="mb-0">
            <thead className="bg-light">
              <tr>
                <th>Fecha</th>
                <th>Empresa</th>
                <th>Descripción</th>
                <th>Factura</th>
                <th>Estado</th>
                <th>Antigüedad</th>
                <th className="text-end">Monto</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.map(t => (
                <tr key={t.id}>
                  <td>{t.date}</td>
                  <td className="fw-bold">{t.entity_name}</td>
                  <td>
                      {t.description}
                      {t.notes && <div className="small text-muted">{t.notes}</div>}
                  </td>
                  <td>{t.invoice_number || '-'}</td>
                  <td>
                     <Badge bg={t.status === 'Por Procesar' ? 'primary' : t.status === 'Esperando OC/HES' ? 'info' : 'warning'}>
                        {t.status}
                     </Badge>
                  </td>
                  <td>{getDaysOverdue(t)} días</td>
                  <td className={`text-end fw-bold ${t.type === 'ingreso' ? '' : 'text-danger'}`}>
                    {t.type === 'ingreso' ? '' : '-'} {formatCurrency(t.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-light fw-bold">
               <tr>
                  <td colSpan="5" className="text-end">Balance Moroso:</td>
                  <td className="text-end">
                     {formatCurrency(filteredTransactions.reduce((acc, t) => t.type === 'ingreso' ? acc + t.amount : acc - t.amount, 0))}
                  </td>
               </tr>
            </tfoot>
          </Table>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default KpiUnpaidPage;